import { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Send, CheckCircle2 } from 'lucide-react';

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ 'form-name': 'contact', ...formData }).toString(),
      });
      setSuccess(true);
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      setError('Error al enviar el mensaje. Por favor, intenta de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contacto" className="relative py-32 px-6 lg:px-12 bg-[#0A0A0A]">
      {/* Decorative elements contained separately so text italic overhang is not clipped */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 grid-bg opacity-20" />
        {/* Top decorative glow */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-30" style={{
          background: 'radial-gradient(circle, rgba(184, 146, 63, 0.4) 0%, transparent 70%)',
          filter: 'blur(80px)'
        }} />
      </div>
      
      <div className="max-w-5xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="font-mono text-gold text-xs tracking-[0.3em] uppercase mb-6 inline-flex items-center gap-3">
            <span className="w-12 h-px bg-gold" />
            Contacto
            <span className="w-12 h-px bg-gold" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-5xl font-light tracking-tight mb-6 leading-[1.05] text-white">
            ¿Listo para
            <br />
            <span className="italic font-medium text-gold-shimmer" style={{ paddingRight: '0.1em' }}>despegar</span>
            <span className="text-burgundy-300">?</span>
          </h2>
          <p className="font-sans text-lg text-white/50 max-w-2xl mx-auto">
            Cuéntanos tu proyecto y trazaremos la ruta perfecta para tu marca.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative dark-card rounded-3xl p-8 lg:p-12 gold-glow"
        >
          <form
            onSubmit={handleSubmit}
            name="contact"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            className="space-y-6"
          >
            <input type="hidden" name="form-name" value="contact" />
            <input type="hidden" name="bot-field" />
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block font-mono text-[10px] tracking-widest uppercase text-white/50 mb-2">
                  Nombre *
                </label>
                <input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  type="text"
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 font-sans focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-200"
                  placeholder="Tu nombre"
                  data-testid="contact-name-input"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block font-mono text-[10px] tracking-widest uppercase text-white/50 mb-2">
                  Email *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 font-sans focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-200"
                  placeholder="tu@empresa.com"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className="block font-mono text-[10px] tracking-widest uppercase text-white/50 mb-2">
                Empresa *
              </label>
              <input
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                type="text"
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 font-sans focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-200"
                placeholder="Nombre de tu empresa"
                data-testid="contact-company-input"
              />
            </div>

            <div>
              <label htmlFor="message" className="block font-mono text-[10px] tracking-widest uppercase text-white/50 mb-2">
                Mensaje *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-white placeholder-white/30 font-sans focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all duration-200 resize-none"
                placeholder="Cuéntanos sobre tu proyecto..."
                data-testid="contact-message-input"
              />
            </div>

            {success && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-gold/10 border border-gold/40 rounded-xl flex items-start gap-3"
                data-testid="contact-success-message"
              >
                <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-display text-base font-medium text-white mb-1">
                    ¡Mensaje enviado!
                  </p>
                  <p className="font-sans text-sm text-white/60">
                    Nos pondremos en contacto contigo muy pronto.
                  </p>
                </div>
              </motion.div>
            )}

            {error && (
              <div className="p-4 bg-burgundy/15 border border-burgundy/50 rounded-xl" data-testid="contact-error-message">
                <p className="font-sans text-sm text-burgundy-200">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="group w-full py-4 bg-gold text-black font-sans font-semibold rounded-full hover:bg-gold-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 relative overflow-hidden gold-glow"
              data-testid="contact-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Enviando...</span>
                </>
              ) : (
                <>
                  <span>Enviar Mensaje</span>
                  <Send className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
